/**
 * Intro - Plays a pre-recorded cinematic video before the game starts.
 * If no video file exists (/intro.webm), skips directly to the game.
 *
 * Flow:
 *   1. Show "Haz clic para comenzar" (required to unlock audio)
 *   2. Play /intro.webm fullscreen with audio
 *   3. When video ends → start the game
 */
export class Intro {
  constructor(onComplete) {
    this.onComplete = onComplete;
    this.done = false;
    // Test hook: ?skipIntro=1 in URL skips video entirely
    if (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('skipIntro') === '1') {
      Promise.resolve().then(() => { try { onComplete(); } catch (e) { console.error(e); } });
      return;
    }
    this._build();
  }

  _build() {
    // Container
    this.container = document.createElement('div');
    this.container.style.cssText =
      'position:fixed;top:0;left:0;right:0;bottom:0;z-index:9999;background:#000;overflow:hidden;';

    // Check if video files actually exist before creating video element
    this._hasVideo = false;
    this._videoCheckDone = false;
    this._checkVideo();

    // Overlay (click to start)
    this.overlay = document.createElement('div');
    this.overlay.style.cssText =
      'position:absolute;top:0;left:0;right:0;bottom:0;z-index:2;display:flex;flex-direction:column;align-items:center;justify-content:center;cursor:pointer;';
    this.overlay.innerHTML = `
      <div style="font-family:monospace;text-align:center;">
        <div style="font-size:72px;font-weight:bold;color:#f0c840;text-shadow:3px 3px 0 rgba(0,0,0,0.4);letter-spacing:4px;">
          MUNDO FEDE
        </div>
        <div id="intro-prompt" style="font-size:17px;color:rgba(255,255,255,0.6);margin-top:60px;">
          Haz clic para comenzar
        </div>
      </div>
    `;
    this.container.appendChild(this.overlay);

    document.body.appendChild(this.container);

    // Pulse animation for prompt
    this._promptEl = this.overlay.querySelector('#intro-prompt');
    this._pulseAnim = setInterval(() => {
      if (!this._promptEl) return;
      const t = Date.now() / 600;
      const a = 0.4 + Math.sin(t) * 0.35;
      this._promptEl.style.color = `rgba(255,255,255,${a})`;
    }, 50);

    // Event: click to play
    this.overlay.addEventListener('click', () => this._play(), { once: true });

    // Event: keyboard
    this._onKey = (e) => {
      if (e.code === 'Space' || e.code === 'Enter') {
        this._play();
        document.removeEventListener('keydown', this._onKey);
      }
    };
    document.addEventListener('keydown', this._onKey);
  }

  _checkVideo() {
    // Use HEAD requests to verify video files exist and have correct content-type
    // Firebase rewrite serves index.html (text/html) for missing files
    this._videoCheckPromise = fetch('/intro.webm', { method: 'HEAD' })
      .then((r) => {
        if (r.ok && r.headers.get('content-type')?.startsWith('video/')) {
          this._hasVideo = '/intro.webm';
          return;
        }
        return fetch('/intro.mp4', { method: 'HEAD' });
      })
      .then((r) => {
        if (r && r.ok && r.headers.get('content-type')?.startsWith('video/')) {
          this._hasVideo = '/intro.mp4';
        }
      })
      .catch(() => {})
      .finally(() => { this._videoCheckDone = true; });
  }

  _play() {
    if (this._playing) return;
    this._playing = true;
    clearInterval(this._pulseAnim);
    document.removeEventListener('keydown', this._onKey);

    // Wait for video check to complete before deciding
    if (!this._videoCheckDone) {
      this._videoCheckPromise.then(() => this._startPlayback());
      return;
    }
    this._startPlayback();
  }

  _startPlayback() {
    // No video file found → skip directly to game
    if (!this._hasVideo) {
      this._finish();
      return;
    }

    // Create video element only when we know the file exists
    this.video = document.createElement('video');
    this.video.style.cssText =
      'position:absolute;top:0;left:0;width:100%;height:100%;object-fit:cover;';
    this.video.playsInline = true;
    this.video.src = this._hasVideo;
    this.container.appendChild(this.video);

    this.overlay.style.display = 'none';

    // Safety timeout: if video doesn't end within 60s, skip
    this._playTimeout = setTimeout(() => this._finish(), 60000);

    this._onEnded = () => this._finish();
    this._onError = () => this._finish();
    this.video.addEventListener('ended', this._onEnded, { once: true });
    this.video.addEventListener('error', this._onError, { once: true });

    const playPromise = this.video.play();
    if (playPromise) {
      playPromise.catch(() => this._finish());
    }
  }

  _finish() {
    if (this.done) return;
    this.done = true;

    clearInterval(this._pulseAnim);
    if (this._playTimeout) clearTimeout(this._playTimeout);
    document.removeEventListener('keydown', this._onKey);

    this.container.style.transition = 'opacity 1s ease-out';
    this.container.style.opacity = '0';

    setTimeout(() => {
      if (this.video) {
        this.video.removeEventListener('ended', this._onEnded);
        this.video.removeEventListener('error', this._onError);
        this.video.pause();
        this.video.src = '';
        this.video = null;
      }
      this.container.remove();
      try { this.onComplete(); } catch (e) { console.error('Intro onComplete error:', e); }
    }, 1200);
  }
}
